import { FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { FlowError, Overview, StepHeader, Layout, Price } from '@eneco/flows/components';
import { getProductIconTypeFromProductType } from '@eneco/flows/helpers';
import { Box, Button, Bleed, IconList, Stack, Text, TextLink, Divider } from '@eneco/sparky';
import { CalendarIcon, ElectricityIcon, RecycleIcon } from '@eneco/sparky/icons';
import { usePlaceholderContent } from '@sitecore/common';
import { OxxioOverviewStepRendering } from '@sitecore/types/OxxioOverviewStep';

import { Ribbon } from '../../../shared/components';
import { getProductTitleFromProductType, getOfferSubTitle, getOfferTitle } from '../../../shared/helpers';
import { useOverviewStepLogic } from '../../../shared/hooks';
import { EnergyType } from '../../../shared/types';
import { PriceDetailsOverviewOxxio as PriceDetailsOverview, FieldsUnionOxxio } from '../../components';

export const OverviewStep: FC = () => {
  const { OxxioOverviewStep: { fields: textualData } = {} } = usePlaceholderContent<{
    OxxioOverviewStep: OxxioOverviewStepRendering;
  }>();

  const {
    flowContext,
    selectedOffer,
    selectedCashbackTexts,
    costsType,
    format,
    currency,
    isSubmitSuccessful,
    setIsSubmitSuccessful,
    isLoading,
    isSuccessful,
    isFailed,
    isNotFound,
    isNotAvailable,
  } = useOverviewStepLogic({
    promotionTexts: textualData?.promotionTexts,
  });

  const { energyType = EnergyType.Electricity } = flowContext;

  const getErrorNotification = () => {
    if (isNotAvailable) return textualData?.genericError?.errorNotAvailableNotification;
    if (isNotFound) return textualData?.genericError?.errorNotFoundNotification;

    return textualData?.genericError?.errorNotification;
  };

  return (
    <Layout.Content variant="B" isLoading={isLoading}>
      {isSuccessful && (
        <>
          <StepHeader textualData={textualData} align="center" />
          <Stack.Item>
            <Overview>
              <Overview.Header withBackground>
                <Stack>
                  <Text weight="bold" size="BodyXL">
                    {selectedOffer && getOfferTitle(selectedOffer, textualData?.productData)}
                  </Text>
                  <Text>{getOfferSubTitle(energyType, textualData?.productTypeLabels)}</Text>
                </Stack>
              </Overview.Header>

              {selectedCashbackTexts && (
                <Bleed top="6">
                  <Ribbon
                    theme="oxxio"
                    size="large"
                    emphasis="medium"
                    title={format(selectedCashbackTexts.title, {
                      [`${selectedOffer?.costDiscounts?.cashback?.type}`]: currency.euroNoFractionDigits(
                        selectedOffer?.costDiscounts?.cashback?.value ?? 0,
                      ),
                    })}
                    text={selectedCashbackTexts?.text}
                    trigger={selectedCashbackTexts?.trigger}
                  />
                </Bleed>
              )}

              {selectedOffer?.products?.map(product => (
                <Overview.Product
                  key={product.type}
                  type={getProductIconTypeFromProductType(product.type)}
                  title={getProductTitleFromProductType(product.type, textualData?.productTypeLabels)}
                  subtitle={product.description}
                  costsMonthly={{
                    value: product.costsMonthly?.[costsType] ?? 0,
                    period: textualData?.costDetails?.perMonthLabel?.value,
                  }}
                  costsYearly={{
                    value: product.costsYearly?.[costsType] ?? 0,
                    period: textualData?.costDetails?.perYearLabel?.value,
                  }}
                />
              ))}

              <Overview.ProductSummary
                label={textualData?.costDetails?.totalCostsLabel?.value}
                costs={{
                  value: selectedOffer?.costsMonthly?.[costsType] ?? 0,
                  period: textualData?.costDetails?.perMonthLabel?.value,
                }}
                costDetailsTrigger={
                  <Text size="BodyS">
                    <TrackedDialog
                      description={<RichText html={textualData?.content?.priceDetailsDialog?.value?.content} />}
                      title={textualData?.content?.priceDetailsDialog?.value?.title}
                      trigger={<TextLink>{textualData?.content?.priceDetailsDialog?.value?.triggerText}</TextLink>}>
                      <Box backgroundColor="backgroundSecondary" paddingX="3">
                        <PriceDetailsOverview
                          offer={selectedOffer}
                          flowContext={flowContext}
                          textualData={textualData as FieldsUnionOxxio}
                        />
                      </Box>
                      <Divider />
                    </TrackedDialog>
                  </Text>
                }>
                <Box paddingTop="2" paddingBottom="1">
                  <Text size="BodyS" align="right">
                    {/* whenever the "costsYearlyPromotion" or the "cashback" value is defined
                      we should render different text label and different price */}
                    <Price
                      suffix={textualData?.costDetails?.[
                        selectedOffer?.costsYearlyPromotion || selectedOffer?.costDiscounts?.cashback?.value
                          ? 'totalCostsFirstYearLabel'
                          : 'perYearLabel'
                      ]?.value?.toLowerCase()}
                      emphasis="low"
                      previousValue={
                        selectedOffer?.costsYearlyPromotion || selectedOffer?.costDiscounts?.cashback?.value
                          ? selectedOffer?.costsYearly?.[costsType]
                          : undefined
                      }>
                      {selectedOffer?.costsYearlyPromotion?.[costsType] ??
                        Number(selectedOffer?.costsYearly?.[costsType] ?? 0) -
                          Number(selectedOffer?.costDiscounts?.cashback?.value ?? 0)}
                    </Price>
                  </Text>
                </Box>
              </Overview.ProductSummary>

              <Overview.Content>
                <Stack direction="column" gap="3">
                  <Button
                    isLoading={isSubmitSuccessful}
                    onClick={() => {
                      setIsSubmitSuccessful(true);
                    }}>
                    {textualData?.content?.nextStepText?.value}
                  </Button>

                  <Text size="BodyS" align="center" color="textLowEmphasis">
                    {textualData?.content?.priceIncludingText?.value}
                  </Text>
                </Stack>
              </Overview.Content>

              <Overview.Footer>
                <IconList>
                  <IconList.Item icon={<RecycleIcon color="iconSecondary" />}>
                    <Text size="BodyS" color="textLowEmphasis">
                      <RichText html={textualData?.content?.usp1Content?.value} />
                    </Text>
                  </IconList.Item>
                  <IconList.Item icon={<ElectricityIcon color="iconSecondary" />}>
                    <Text size="BodyS" color="textLowEmphasis">
                      <RichText html={textualData?.content?.usp2Content?.value} />
                    </Text>
                  </IconList.Item>
                  <IconList.Item icon={<CalendarIcon color="iconSecondary" />}>
                    <Text size="BodyS" color="textLowEmphasis">
                      <RichText html={textualData?.content?.usp3Content?.value} />
                    </Text>
                  </IconList.Item>
                </IconList>
              </Overview.Footer>
            </Overview>
          </Stack.Item>
        </>
      )}

      {isFailed && (
        <FlowError
          textualData={{
            notification: getErrorNotification(),
            buttonLabel: !isNotAvailable && !isNotFound ? textualData?.genericError?.tryAgainButtonText : undefined,
          }}
        />
      )}
    </Layout.Content>
  );
};
